"use client";

/**
 * Step 1: Services Selection
 * Large touch-friendly cards for easy mobile interaction
 */

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { ServiceInfo } from "@/types/tenant-landing-v2";
import { Check, Clock, DollarSign } from "lucide-react";
import { useState } from "react";

interface ServicesStepProps {
  services: ServiceInfo[];
  selectedIds: string[];
  onSelect: (ids: string[]) => void;
}

export function ServicesStep({
  services,
  selectedIds,
  onSelect,
}: ServicesStepProps) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredServices = services.filter(
    (service) =>
      service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleService = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelect(selectedIds.filter((sid) => sid !== id));
    } else {
      onSelect([...selectedIds, id]);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Compact info banner */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 shrink-0">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          💡 Selecione um ou mais serviços para agendar
        </p>
      </div>

      {/* Search if many services */}
      {services.length > 5 && (
        <div className="relative shrink-0">
          <input
            type="text"
            placeholder="Buscar serviço..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 pl-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            🔍
          </div>
        </div>
      )}

      {/* Services grid - Scrollable */}
      <div className="flex-1 overflow-y-auto -mx-4 md:mx-0 px-4 md:px-0 overscroll-contain">
        <div className="grid gap-3 pb-4">
          {filteredServices.length === 0 && (
            <p className="text-center text-muted-foreground py-8">
              Nenhum serviço encontrado com &ldquo;{searchQuery}&rdquo;
            </p>
          )}

          {filteredServices.map((service) => {
            const isSelected = selectedIds.includes(service.id);
            return (
              <Card
                key={service.id}
                onClick={() => toggleService(service.id)}
                className={`relative cursor-pointer p-0 transition-all hover:shadow-md active:scale-[0.98] ${
                  isSelected
                    ? "border-primary border-2 bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <div className="p-4">
                  {/* Selection indicator */}
                  <div
                    className={`absolute top-3 right-3 h-6 w-6 rounded-full border-2 flex items-center justify-center transition-all ${
                      isSelected
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-muted bg-background"
                    }`}
                  >
                    {isSelected && <Check className="h-4 w-4" />}
                  </div>

                  {/* Service info */}
                  <div className="pr-10">
                    <h3 className="font-semibold text-base mb-1">
                      {service.name}
                    </h3>
                    {service.description && (
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                        {service.description}
                      </p>
                    )}

                    {/* Meta info */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <Badge variant="secondary" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDuration(service.duration)}
                      </Badge>

                      {service.hasVariablePrice ? (
                        <Badge variant="outline" className="text-xs">
                          <DollarSign className="h-3 w-3 mr-1" />
                          Preço sob consulta
                        </Badge>
                      ) : (
                        <Badge
                          variant="default"
                          className="text-xs font-semibold"
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          {formatCurrency(service.price / 100)}
                        </Badge>
                      )}

                      {service.requiresBookingFee && (
                        <Badge variant="destructive" className="text-xs">
                          Taxa de reserva
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Sticky selected summary */}
      {selectedIds.length > 0 && (
        <div className="shrink-0 bg-muted/50 border-t -mx-4 md:mx-0 px-4 md:px-0 py-3 -mb-4 md:-mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {selectedIds.length} serviço{selectedIds.length > 1 ? "s" : ""}{" "}
              selecionado{selectedIds.length > 1 ? "s" : ""}
            </span>
            <span className="font-semibold">
              {formatDuration(
                services
                  .filter((s) => selectedIds.includes(s.id))
                  .reduce((sum, s) => sum + s.duration, 0)
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
